import {CoinsBannerSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useQuantityFormat, useTranslation} from '@aboutyou/i18n';
import {
  AboutYouSVGLogo,
  COLORS,
  ContentPopperProps,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  RichText,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useIsCoinsActive} from '../../../features/Coins/utils/useIsCoinsActive';
import {CoinsBannerPopup} from './CoinsBannerPopup/CoinsBannerPopup';
import coinsBackgroundLight from './assets/banner-background.jpg';
import {useCoinsBannerStateManager} from './utils/useCoinsBannerStateManager';

export const CoinsBannerView: React.FC<
  CoinsBannerSection & {
    className?: string;
    ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
  }
> = ({
  className,
  coinsInfoSheet,
  coinsToBeEarned,
  ContentPopperLayoutComponent,
}) => {
  const isCoinsActive = useIsCoinsActive();

  const format = useQuantityFormat();
  const formattedCoinsToBeEarned = format(coinsToBeEarned);

  const {t} = useTranslation();
  const {open} = useCoinsBannerStateManager();

  if (!isCoinsActive) {
    return null;
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      open();
    }
  };

  return (
    <>
      <Wrapper
        data-testid="coinsBanner"
        className={className}
        onClick={() => open()}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <Header>
          <HeaderLogo inert>
            <StyledAboutYouSVGLogo disableLogoLink />
            <Text size="px16" weight="mediumBold">
              {t('Coins')}
            </Text>
          </HeaderLogo>
          <Icon asset={GenericIconName.ArrowSmallRight} size="S" />
        </Header>
        <StyledRichText>
          {t(
            `Buy this item now and earn up to <span style="color: {coinsColor};">+{coinsToBeEarned} Coins</span> that you can redeem into discounts.`,
            {
              coinsToBeEarned: formattedCoinsToBeEarned,
              coinsColor: COLORS['Alerts.Success.Primary'],
            },
          )}
        </StyledRichText>
      </Wrapper>
      <CoinsBannerPopup
        LayoutComponent={ContentPopperLayoutComponent}
        {...coinsInfoSheet}
      />
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;
  border-radius: 4px;
  background: url(${coinsBackgroundLight});
  background-size: cover;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

const Header = styled.div`
  display: flex;
`;

const HeaderLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const StyledAboutYouSVGLogo = styled(AboutYouSVGLogo)`
  > svg {
    width: 121px;
    height: 24px;
  }
`;

const StyledRichText = styled(RichText)`
  line-height: 1.4;
  font-weight: ${FONT_WEIGHTS.mediumBold};
`;
